/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useRef} from 'react';
import {usePreferencesState} from '@ion/core/src/preferences/hooks/usePreferencesState';
import {Preference} from '@ion/core/src/types/preferences';

export interface UseGridPageSizeParams {
  usePreferedPageSize?: boolean;
  defaultPageSize?: number;
  setGridPageSize?: (pageSize: number) => void;
  preferenceKey?: Preference;
};

/**
 *
 * React Hook to provide utililty functions for saving grid Page Size in any Grid.
 *
 *  ```typescript
 *
 *   import {useGridPageSize} from '@/dls/src/grid';
 *
 *   const [preferredPageSize, setPreferredPageSize, preferredPageSize] = useGridPageSize();
 *
 *  ```
 */
export const useGridPageSize = ({
  setGridPageSize,
  defaultPageSize = 20,
  usePreferedPageSize = false,
  preferenceKey = Preference.gridRowsPerPage
}: UseGridPageSizeParams) => {
  const [pageSize, _setPageSize] = usePreferencesState(
    preferenceKey,
    defaultPageSize,
    usePreferedPageSize
  );
  const pageSizeRef = useRef(pageSize);

  const setPageSize = useCallback((size: number) => {
    _setPageSize(size, usePreferedPageSize);
    pageSizeRef.current = size;
    if (!usePreferedPageSize) {
      setGridPageSize?.(size);
    }
  }, [usePreferedPageSize, _setPageSize]);

  // when usePreferedPageSize is false, the caller (not a sticky user preference) owns
  // page size and may swap defaultPageSize reactively (e.g. per-tab persisted values) -
  // without this, pageSize stays stuck at whichever value was current on first mount...
  useEffect(() => {
    if (!usePreferedPageSize && defaultPageSize !== pageSizeRef.current) {
      _setPageSize(defaultPageSize, usePreferedPageSize);
      pageSizeRef.current = defaultPageSize;
    }
  }, [usePreferedPageSize, defaultPageSize]);

  return [pageSize, setPageSize, pageSizeRef];
};
